import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '../../lib/supabase';
import PhoneInput from '../PhoneInput';
import DuplicateCustomerModal from '../DuplicateCustomerModal';
import {
  hoursForDate,
  applyDateException,
  computeAvailableSlots,
  formatWallMin,
} from '../../lib/availability';
import { dateKeyOf, parseWallToMin } from '../../lib/diary/helpers';

// ===== NEW BOOKING MODAL =====
export const NewBookingModal = ({
  shopId, hoursByBarber, exceptionsByBarber, breaksByBarber,
  allBookings, prefill, onClose, onCreated,
}) => {
  const [services, setServices] = useState([]);
  const [barbers, setBarbers]   = useState([]);
  const [form, setForm] = useState({
    booking_type: 'walkin',              // 'walkin' | 'prebooked' — drives payment UI
    name: '', phone: '', phone_valid: false, email: '', is_non_uk_number: false,   // UK-first — matches the shop's operating market
    barber_id: prefill?.barber_id || '',
    service_id: '',
    date: prefill?.date || new Date().toISOString().slice(0, 10),
    time: prefill?.time || '10:00',
    mark_paid: false,
    paid_method: '',                     // '' | 'cash' | 'card' — captured when mark_paid=true
  });
  // Customer typeahead
  const [customerSuggestions, setCustomerSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [customerSearchToken, setCustomerSearchToken] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [duplicateCandidate, setDuplicateCandidate] = useState(null);
  // Booking payload the user just submitted — held while the duplicate
  // modal asks whether to reuse the existing client.
  const [pendingCreatePayload, setPendingCreatePayload] = useState(null);
  // Multi-part form: 1 = Details, 2 = Slot, 3 = Confirm
  const [step, setStep] = useState(1);

  useEffect(() => {
    (async () => {
      const [{ data: svc }, { data: bb }] = await Promise.all([
        supabase.from('services').select('*').eq('shop_id', shopId).eq('active', true).order('sort_order'),
        supabase.from('barbers').select('*').eq('shop_id', shopId).eq('active', true).order('created_at'),
      ]);
      setServices(svc || []);
      setBarbers(bb || []);
      setForm(f => ({
        ...f,
        service_id: f.service_id || svc?.[0]?.id || '',
        barber_id:  f.barber_id  || bb?.[0]?.id  || '',
      }));
    })();
  }, [shopId]);

  // ---- Availability computation for the picked (barber + date + service) ----
  const svc = services.find(s => s.id === form.service_id);
  const durationMin = svc?.duration_min || 30;
  const pickedDate = form.date ? new Date(`${form.date}T00:00:00`) : null;
  const dKey = pickedDate ? dateKeyOf(pickedDate) : null;
  const weeklyH = pickedDate && form.barber_id ? hoursForDate(hoursByBarber, form.barber_id, pickedDate) : { open: false };
  const dayException = form.barber_id && dKey ? exceptionsByBarber?.[form.barber_id]?.[dKey] : null;
  const h = applyDateException(weeklyH, dayException);
  const breaksToday = form.barber_id && dKey ? (breaksByBarber?.[form.barber_id]?.[dKey] || []) : [];
  const bksSameDayBarber = useMemo(() => {
    if (!pickedDate || !form.barber_id) return [];
    return allBookings.filter(b =>
      b.barber_id === form.barber_id &&
      new Date(b.starts_at).toDateString() === pickedDate.toDateString()
    );
  }, [allBookings, form.barber_id, form.date, pickedDate]);

  // Per-barber buffer between bookings — read from the currently
  // selected barber. Applied inside computeAvailableSlots so the slot
  // grid respects the barber's own configured gap.
  const selectedBarber = barbers.find(b => b.id === form.barber_id);
  const bufferMin = Number(selectedBarber?.booking_buffer_minutes || 0);

  const availableSlots = useMemo(() => {
    // If picked date is today, hide slots earlier than the current
    // wall-clock so shop admins can't accidentally save a booking in
    // the past (walk-in path was allowing 09:00 at 11:00 on the same
    // day and creating an already-elapsed booking).
    let minStartMin;
    if (pickedDate && pickedDate.toDateString() === new Date().toDateString()) {
      const now = new Date();
      minStartMin = now.getHours() * 60 + now.getMinutes();
    }
    return computeAvailableSlots({
      hours: h,
      durationMin,
      bookings: bksSameDayBarber,
      breaks: breaksToday,
      stepMin: 15,
      bufferMin,
      minStartMin,
    });
  }, [h.open, h.startMin, h.endMin, durationMin, bksSameDayBarber, breaksToday, bufferMin, pickedDate]);

  // Guard against stale prefill: if form.time is no longer in the
  // available set (e.g. barber's buffer now hides that slot after the
  // barber row loaded, or the picked date/service changed), clear it
  // so step 2's Next button doesn't let a hidden slot survive to save.
  // Skip while the slot set is transiently empty (loading or closed day)
  // so a valid prefill isn't wiped mid-render.
  useEffect(() => {
    if (!form.time) return;
    if (!availableSlots || availableSlots.length === 0) return;
    const [hh, mm] = form.time.split(':').map(Number);
    const cur = hh * 60 + (mm || 0);
    if (!availableSlots.includes(cur)) {
      setForm(f => ({ ...f, time: '' }));
    }
  }, [availableSlots, form.time]);

  // First slot ≥ current wall-clock when the picked date is today. Used
  // to power the "Next free: HH:MM" one-tap button on the walk-in path.
  const nextFreeMin = useMemo(() => {
    if (!availableSlots.length) return null;
    const today = new Date().toISOString().slice(0, 10);
    if (form.date !== today) return availableSlots[0];
    const now = new Date();
    const nowMin = now.getHours() * 60 + now.getMinutes();
    return availableSlots.find(m => m >= nowMin) ?? availableSlots[0];
  }, [availableSlots, form.date]);

  // Customer typeahead: shop-scoped search against the /api/customers
  // endpoint. Debounced 200ms. Aborts stale responses via a token counter.
  useEffect(() => {
    const q = (form.name || '').trim();
    if (q.length < 2) { setCustomerSuggestions([]); return; }
    const token = customerSearchToken + 1;
    setCustomerSearchToken(token);
    const t = setTimeout(async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/customers/search?q=${encodeURIComponent(q)}&shop_id=${encodeURIComponent(shopId)}`;
        const res = await fetch(url, {
          headers: { 'Authorization': `Bearer ${session?.access_token}` },
        });
        if (!res.ok) return;
        const j = await res.json();
        // Ignore stale responses
        if (token !== customerSearchToken + 1) return;
        setCustomerSuggestions(Array.isArray(j) ? j : []);
      } catch (_e) { /* silent */ }
    }, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.name, shopId]);

  const applyCustomer = (c) => {
    setForm(f => ({
      ...f,
      name: c.name || f.name,
      phone: c.phone || f.phone,
      // Trust existing customer records — the phone was validated when
      // originally captured, so an autofilled value passes step-1 gate.
      phone_valid: !!(c.phone && String(c.phone).trim().length > 0) || f.phone_valid,
      email: c.email || f.email,
    }));
    setShowSuggestions(false);
  };

  const closedMsg = !form.barber_id || !form.date ? null
    : !h.open
      ? (dayException?.is_closed
          ? `${barbers.find(b => b.id === form.barber_id)?.name?.split(' ')[0] || 'This barber'} has this day off. Pick another day or barber.`
          : `${barbers.find(b => b.id === form.barber_id)?.name?.split(' ')[0] || 'This barber'} is closed on ${['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][pickedDate.getDay()]}s. Pick another day or barber.`)
      : availableSlots.length === 0
        ? `No ${durationMin}-min slots free on this day for this barber.`
        : null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Only allow submission from the final step — guards against React
    // reusing button DOM nodes across step transitions and firing a
    // stray form submit when the user clicks "Next".
    if (step !== 3) return;
    setError('');
    setSubmitting(true);
    try {
      if (!svc) throw new Error('Pick a service');
      const startsAt = new Date(`${form.date}T${form.time}:00`);
      const endsAt   = new Date(startsAt.getTime() + svc.duration_min * 60000);

      // Belt-and-braces overlap check on the DB (proper interval).
      // Include 'pending' so deposit-in-flight bookings still block the slot.
      const { data: conflict } = await supabase
        .from('bookings')
        .select('id')
        .eq('barber_id', form.barber_id)
        .in('status', ['pending', 'confirmed', 'completed'])
        .lt('starts_at', endsAt.toISOString())
        .gt('ends_at',   startsAt.toISOString());
      if (conflict && conflict.length) throw new Error('That slot conflicts with an existing booking for this barber.');

      // Also verify barber_hours + date-exception covers the range
      const weeklyCheck = hoursForDate(hoursByBarber, form.barber_id, startsAt);
      const excCheck = exceptionsByBarber?.[form.barber_id]?.[dateKeyOf(startsAt)] || null;
      const hCheck = applyDateException(weeklyCheck, excCheck);
      const startMin = startsAt.getHours() * 60 + startsAt.getMinutes();
      const endMin   = endsAt.getHours()   * 60 + endsAt.getMinutes();
      if (!hCheck.open || startMin < hCheck.startMin || endMin > hCheck.endMin) {
        throw new Error('That time falls outside the barber\u2019s working hours.');
      }
      // Belt-and-braces break overlap check
      const breaksCheck = breaksByBarber?.[form.barber_id]?.[dateKeyOf(startsAt)] || [];
      const clashBreak = breaksCheck.find(br => {
        const bs = parseWallToMin(br.start_time);
        const be = parseWallToMin(br.end_time);
        return bs != null && be != null && startMin < be && endMin > bs;
      });
      if (clashBreak) {
        throw new Error(`That time clashes with a scheduled break (${clashBreak.label || 'Break'}).`);
      }

      // Phone validation removed from this surface — the non-UK toggle
      // was pulled from the modal, so we accept any format the barber
      // types (or a blank phone). Server-side duplicate detection still
      // works on whatever string is provided.

      // ------- Duplicate detection -------
      // Ask the RPC whether this shop already has a customer matching
      // by phone (or email, if the phone is blank/non-UK). If yes,
      // pop the "This number already belongs to X" modal so the shop
      // admin explicitly chooses to reuse or edit.
      const { data: dupData } = await supabase.rpc('check_customer_duplicate', {
        _shop_id: shopId,
        _email:   form.email || '',
        _phone:   form.is_non_uk_number ? '' : (form.phone || ''),
      });
      if (dupData && dupData.id) {
        // Stash the payload and pop the modal. The user's next click
        // ("Use existing" / "Change my entry") resumes or aborts.
        setDuplicateCandidate(dupData);
        setPendingCreatePayload({ startsAt, endsAt, svc });
        setSubmitting(false);
        return;
      }

      // No duplicate — insert a fresh customer. Uses the authenticated
      // dedupe RPC so the INSERT goes through a SECURITY DEFINER path
      // (staff INSERTs on `customers` are now blocked by RLS unless
      // routed through this RPC — see `batch-c-customers-insert.sql`).
      const { data: newCustId, error: ce } = await supabase.rpc(
        'find_or_create_customer_authed',
        {
          _shop_id:          shopId,
          _name:             form.name,
          _email:            form.email || '',
          _phone:            form.phone || '',
          _is_non_uk_number: !!form.is_non_uk_number,
        },
      );
      if (ce) throw ce;
      if (!newCustId) throw new Error('Customer create returned no id');
      await finaliseBooking(newCustId, { startsAt, endsAt, svc });
    } catch (err) {
      setError(err.message || 'Could not create booking');
    } finally {
      setSubmitting(false);
    }
  };

  // Extracted so the "Use existing record" click in the duplicate
  // modal reuses exactly the same booking-insert logic.
  const finaliseBooking = async (customerId, { startsAt, svc }) => {
    const paid = !!form.mark_paid;
    const method = paid ? form.paid_method : null;
    // F-01: staff bookings go through create_admin_booking (SECURITY
    // DEFINER RPC). The RPC enforces auth + shop membership, derives
    // ends_at from service.duration_min, generates the cancellation
    // token, and writes the 'created' audit row in the same
    // transaction — matching what create_public_booking gives the
    // anon path.
    const { data: newId, error: be } = await supabase.rpc('create_admin_booking', {
      _shop_id:      shopId,
      _customer_id:  customerId,
      _barber_id:    form.barber_id,
      _service_id:   form.service_id,
      _starts_at:    startsAt.toISOString(),
      _paid_in_full: paid,
      _paid_method:  method,
      _channel:      'manual',
    });
    if (be) {
      if (be.code === '23P01') {
        // `bookings_no_overlap` EXCLUSION constraint fired — the RPC
        // deliberately doesn't gate admin overlaps in application code
        // (overrides for hours / breaks / notice / window are allowed),
        // but true overlaps are hard-blocked at the DB layer.
        const bName = barbers.find(b => b.id === form.barber_id)?.name || 'this barber';
        throw new Error(`That time overlaps an existing booking for ${bName}. Pick another slot or reschedule the existing one.`);
      }
      if (be.code === '42501') {
        throw new Error('You don\u2019t have permission to create bookings. Contact your shop admin.');
      }
      throw be;
    }
    if (!newId) {
      throw new Error('Booking could not be saved. Refresh the page and try again — if this keeps happening, contact support.');
    }
    // Fire the deposit-link email for pending prebooked bookings only.
    // Walk-ins and bookings marked as already-paid skip this entirely —
    // the UI copy also varies accordingly (see nb-submit label).
    const needsDepositEmail = form.booking_type === 'prebooked' && !paid;
    if (needsDepositEmail) {
      try {
        const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/email/send-deposit-link`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ booking_id: newId }),
        });
        const body = await resp.json().catch(() => ({}));
        if (!resp.ok || body.sent === false) {
          // Non-fatal: the booking is already saved. Surface a soft
          // warning via onCreated's second arg if the parent cares.
          console.warn('[deposit-link] email not sent:', body?.detail || body?.error || resp.status);
        }
      } catch (e) {
        // Logged, never thrown — the booking exists; the admin can
        // resend from the diary.
        console.warn('[deposit-link] send failed:', e);
      }
    }
    // Notify listening surfaces (shop Dashboard) that KPIs are stale.
    try { window.dispatchEvent(new CustomEvent('instacut:booking-changed')); } catch { /* no-op */ }
    onCreated();
  };

  // Duplicate-modal callbacks.
  const handleUseExisting = async () => {
    if (!duplicateCandidate || !pendingCreatePayload) return;
    setSubmitting(true);
    try {
      await finaliseBooking(duplicateCandidate.id, pendingCreatePayload);
    } catch (err) {
      setError(err.message || 'Could not attach booking to existing client');
    } finally {
      setSubmitting(false);
      setDuplicateCandidate(null);
      setPendingCreatePayload(null);
    }
  };
  const handleEditEntry = () => {
    setDuplicateCandidate(null);
    setPendingCreatePayload(null);
  };

  const pickSlot = (min) => setForm(f => ({
    ...f,
    time: formatWallMin(min),
  }));

  const currentMin = (() => {
    if (!form.time) return null;
    const [hh, mm] = form.time.split(':').map(Number);
    return hh * 60 + (mm || 0);
  })();

  // Step validation — determines whether "Next" is enabled
  // Phone is optional here; non-UK toggle is hidden and validation is
  // always lenient. Server still gets the raw string.
  // Step 1 gate: name + booking_type + a phone that PhoneInput has
  // reported as valid. Email stays optional. Non-UK numbers are
  // accepted as long as the field has some content.
  const phoneOk = form.is_non_uk_number
    ? (form.phone || '').trim().length >= 4
    : !!form.phone_valid;
  const step1Valid = form.name.trim().length > 0 && !!form.booking_type && phoneOk;
  const step2Valid = !!form.barber_id && !!form.service_id && !!form.date && !!form.time && !closedMsg && availableSlots.some(m => {
    const [hh, mm] = form.time.split(':').map(Number);
    return m === hh * 60 + (mm || 0);
  });
  const goNext = () => setStep(s => Math.min(3, s + 1));
  const goBack = () => { setError(''); setStep(s => Math.max(1, s - 1)); };

  // Human-friendly copy for the confirm step
  const dateLabel = form.date
    ? new Date(`${form.date}T00:00:00`).toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short' })
    : '—';
  const barberName = barbers.find(b => b.id === form.barber_id)?.name || '—';

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <form className="modal-card" onClick={(e) => e.stopPropagation()} onSubmit={handleSubmit} data-testid="new-booking-modal">
        <button
          type="button"
          className="modal-close"
          aria-label="Close"
          onClick={onClose}
          data-testid="nb-close"
        >
          ×
        </button>
        <h3>New booking</h3>
        <div className="nb-stepper" role="tablist" aria-label="Booking steps" data-testid="nb-stepper">
          {[
            { n: 1, label: 'Details' },
            { n: 2, label: 'Slot' },
            { n: 3, label: 'Confirm' },
          ].map(({ n, label }, i, arr) => (
            <React.Fragment key={n}>
              <div className={`nb-step ${step === n ? 'active' : ''} ${step > n ? 'done' : ''}`} data-testid={`nb-step-${n}`}>
                <span className="nb-step-num">{step > n ? '✓' : n}</span>
                <span className="nb-step-lbl">{label}</span>
              </div>
              {i < arr.length - 1 && <div className={`nb-step-bar ${step > n ? 'done' : ''}`} />}
            </React.Fragment>
          ))}
        </div>
        {error && <div className="modal-error" data-testid="new-booking-error">{error}</div>}

        {/* ---------------- STEP 1 · DETAILS ---------------- */}
        {step === 1 && (
          <div className="nb-step-body" data-testid="nb-body-details">
            <div className="field">
              <label>Booking type</label>
              <div className="segbar" role="tablist" aria-label="Booking type" data-testid="nb-type">
                <button
                  type="button"
                  role="tab"
                  aria-selected={form.booking_type === 'walkin'}
                  className={`seg ${form.booking_type === 'walkin' ? 'on' : ''}`}
                  onClick={() => setForm({ ...form, booking_type: 'walkin', date: new Date().toISOString().slice(0, 10) })}
                  data-testid="nb-type-walkin"
                >Walk-in</button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={form.booking_type === 'prebooked'}
                  className={`seg ${form.booking_type === 'prebooked' ? 'on' : ''}`}
                  onClick={() => setForm({ ...form, booking_type: 'prebooked', mark_paid: false, paid_method: '' })}
                  data-testid="nb-type-prebooked"
                >Prebooked</button>
              </div>
              <div className="hint-sub">
                {form.booking_type === 'walkin'
                  ? 'Today · payment collected at the chair.'
                  : 'Future date · customer pays a deposit online.'}
              </div>
            </div>

            <div className="field" style={{ position: 'relative' }}>
              <label>Customer name</label>
              <input className="inp" required value={form.name}
                onChange={(e) => { setForm({ ...form, name: e.target.value }); setShowSuggestions(true); }}
                onFocus={() => setShowSuggestions(true)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                autoComplete="off"
                data-testid="nb-name" />
              {showSuggestions && customerSuggestions.length > 0 && (
                <div className="nb-suggest" data-testid="nb-name-suggestions">
                  {customerSuggestions.slice(0, 6).map(c => (
                    <button
                      key={c.id}
                      type="button"
                      className="nb-suggest-item"
                      onClick={() => applyCustomer(c)}
                      data-testid={`nb-name-suggestion-${c.id}`}
                    >
                      <span className="nb-suggest-nm">{c.name}</span>
                      {c.phone && <span className="nb-suggest-meta">{c.phone}</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="field-row">
              <div className="field">
                <PhoneInput
                  value={form.phone}
                  isNonUK={form.is_non_uk_number}
                  onChange={({ value, isNonUK, isValid }) =>
                    setForm({ ...form, phone: value, is_non_uk_number: isNonUK, phone_valid: !!isValid })
                  }
                  required
                  hideNonUKToggle
                  testIdPrefix="nb-phone"
                />
              </div>
              <div className="field">
                <label>Email</label>
                <input className="inp" type="email" value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="nb-email" />
              </div>
            </div>
          </div>
        )}

        {/* ---------------- STEP 2 · SLOT ---------------- */}
        {step === 2 && (
          <div className="nb-step-body" data-testid="nb-body-slot">
            <div className="field-row">
              <div className="field">
                <label>Date</label>
                <input className="inp" type="date" value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })} required data-testid="nb-date" />
              </div>
              <div className="field">
                <label>Barber</label>
                <select className="inp" value={form.barber_id}
                  onChange={(e) => setForm({ ...form, barber_id: e.target.value })} required data-testid="nb-barber">
                  {barbers.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
            </div>
            <div className="field">
              <label>Service</label>
              <select className="inp" value={form.service_id}
                onChange={(e) => setForm({ ...form, service_id: e.target.value })} required data-testid="nb-service">
                {services.map(s => <option key={s.id} value={s.id}>{s.name} · {s.duration_min}m</option>)}
              </select>
            </div>
            <div className="field">
              <label>Available slots {svc ? `(${svc.duration_min}-min ${svc.name})` : ''}</label>
              {closedMsg ? (
                <div style={{
                  padding: '10px 12px',
                  background: 'var(--paper)',
                  border: '1px solid var(--line)',
                  borderRadius: 8,
                  fontSize: 12.5,
                  color: 'var(--mute)',
                }} data-testid="nb-closed-msg">
                  {closedMsg}
                </div>
              ) : (
                <>
                  {nextFreeMin != null && form.booking_type === 'walkin' && (
                    <button
                      type="button"
                      className="btn p"
                      onClick={() => pickSlot(nextFreeMin)}
                      style={{ marginBottom: 8, width: '100%' }}
                      data-testid="nb-next-free"
                    >
                      Next free: {formatWallMin(nextFreeMin)} — tap to select
                    </button>
                  )}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, maxHeight: 200, overflowY: 'auto' }} data-testid="nb-slot-picker">
                    {availableSlots.map(min => {
                      const label = formatWallMin(min);
                      const selected = currentMin === min;
                      return (
                        <button
                          key={min}
                          type="button"
                          className={`btn ${selected ? 'p' : 's'}`}
                          onClick={() => pickSlot(min)}
                          style={{ padding: '6px 10px', fontSize: 12.5 }}
                          data-testid={`nb-slot-${label.replace(':', '')}`}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* ---------------- STEP 3 · CONFIRM ---------------- */}
        {step === 3 && (
          <div className="nb-step-body" data-testid="nb-body-confirm">
            <div className="nb-summary" data-testid="nb-summary">
              <div className="nb-summary-row">
                <span className="nb-summary-k">Type</span>
                <span className="nb-summary-v" style={{ textTransform: 'capitalize' }}>{form.booking_type}</span>
              </div>
              <div className="nb-summary-row">
                <span className="nb-summary-k">Customer</span>
                <span className="nb-summary-v">{form.name}</span>
              </div>
              {(form.phone || form.email) && (
                <div className="nb-summary-row">
                  <span className="nb-summary-k">Contact</span>
                  <span className="nb-summary-v nb-summary-meta">
                    {form.phone}{form.phone && form.email ? ' · ' : ''}{form.email}
                  </span>
                </div>
              )}
              <div className="nb-summary-row">
                <span className="nb-summary-k">Barber</span>
                <span className="nb-summary-v">{barberName}</span>
              </div>
              <div className="nb-summary-row">
                <span className="nb-summary-k">Service</span>
                <span className="nb-summary-v">{svc?.name || '—'} · {svc?.duration_min}m · £{svc?.price}</span>
              </div>
              <div className="nb-summary-row">
                <span className="nb-summary-k">When</span>
                <span className="nb-summary-v">{dateLabel} · {form.time}</span>
              </div>
            </div>

            {form.booking_type === 'walkin' ? (
              <div className="field" style={{ marginTop: 12 }}>
                <label style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-mute)', display: 'block', marginBottom: 6 }}>
                  Payment {svc ? `(£${svc.price})` : ''}
                </label>
                <div className="segbar" role="radiogroup" aria-label="Payment method" data-testid="nb-payment-picker">
                  <button
                    type="button"
                    role="radio"
                    aria-checked={!form.mark_paid}
                    className={`seg ${!form.mark_paid ? 'on' : ''}`}
                    onClick={() => setForm({ ...form, mark_paid: false, paid_method: '' })}
                    data-testid="nb-pay-unpaid"
                  >Unpaid</button>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={form.mark_paid && form.paid_method === 'cash'}
                    className={`seg ${form.mark_paid && form.paid_method === 'cash' ? 'on' : ''}`}
                    onClick={() => setForm({ ...form, mark_paid: true, paid_method: 'cash' })}
                    data-testid="nb-pay-cash"
                  >Paid — Cash</button>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={form.mark_paid && form.paid_method === 'card'}
                    className={`seg ${form.mark_paid && form.paid_method === 'card' ? 'on' : ''}`}
                    onClick={() => setForm({ ...form, mark_paid: true, paid_method: 'card' })}
                    data-testid="nb-pay-card"
                  >Paid — Card</button>
                </div>
              </div>
            ) : (
              <div className="nb-hint" data-testid="nb-deposit-hint" style={{ marginTop: 12 }}>
                Booking will be created as <b>pending</b>. Customer receives a deposit link by email — the slot is held until the deposit clears.
              </div>
            )}
          </div>
        )}

        {/* ---------------- Footer actions ---------------- */}
        <div className="modal-actions">
          <button type="button" className="btn s" onClick={step === 1 ? onClose : goBack} data-testid={step === 1 ? 'nb-cancel' : 'nb-back'}>
            {step === 1 ? 'Cancel' : 'Back'}
          </button>
          {step < 3 ? (
            <button
              key="nb-next"
              type="button"
              className="btn p"
              onClick={goNext}
              disabled={(step === 1 && !step1Valid) || (step === 2 && !step2Valid)}
              data-testid="nb-next"
            >
              Next →
            </button>
          ) : (
            <button key="nb-save" type="submit" className="btn p" disabled={submitting || !!closedMsg} data-testid="nb-submit">
              {submitting ? 'Saving…' : (form.booking_type === 'walkin' ? 'Save walk-in' : 'Save & send deposit link')}
            </button>
          )}
        </div>
      </form>
      {duplicateCandidate && (
        <DuplicateCustomerModal
          existing={duplicateCandidate}
          matchedField={duplicateCandidate.matched_on}
          onUseExisting={handleUseExisting}
          onEdit={handleEditEntry}
        />
      )}
    </div>
  );
};
